import TickerTimeout from "../../utils/TickerTimeout";
import store from "../../State/Store/store";
import {
  setPotCollectionScreenProgressProcessing,
  advancePotCollectionScreenProgress,
  incrementPotCollectionCoinCount,
} from "../../State/actions";
import PotCollectionFeatureExecutor from "./PotCollectionFeatureExecutor";
import { getSymbolZIndex } from "../../SymbolData";
import { FEATURES_CONFIG } from "../../featuresConfig";


// ─── Action Type Constants ──────────────────────────────────────────────────

/**
 * BE screen_progress action types.
 *
 * Format per action:
 *   { action: "new_coins", coordinates: [reel, row] }
 *   { action: "m_reveals", coordinates: [reel, row], tier: "minor", value: 50 }
 *   { action: "d_doubles", coordinates: [[reel, row], [reel, row]], value_map: {...} }
 *   { action: "d_reveals", coordinates: [reel, row], value: 100 }
 *   { action: "c_collect", coordinates: [reel, row], source_coins: [...], total: 500 }
 */
const ACTION_TYPES = {
  NEW_COINS: "new_coins",
  M_REVEALS: "m_reveals",
  D_DOUBLES: "d_doubles",
  D_REVEALS: "d_reveals",
  C_COLLECT: "c_collect",
};

/**
 * PotCollectionScreenProgressProcessor
 *
 * Processes the sequential `screen_progress` array from BE respin responses.
 * Each action is executed one-at-a-time; the processor awaits completion
 * before advancing to the next action.
 *
 * GDD: §15 feature execution order, §13.2 respin counter rules
 *
 * Usage:
 *   const processor = new PotCollectionScreenProgressProcessor({ ... });
 *   await processor.processAll(screenProgressArray, dispatch);
 *   // All actions done — safe to schedule next spin
 */
class PotCollectionScreenProgressProcessor {
  /**
   * @param {Object} params
   * @param {Object} params.overlayReelRefs - PC overlay reel refs
   * @param {Object} params.reelRefs - PC static reel refs
   * @param {PIXI.Container} params.collectionLayerContainer - For value travelers
   * @param {number} params.symbolWidth
   * @param {number} params.symbolHeight
   * @param {number} params.reelSpacing
   * @param {Function} [params.onCoinLanded] - Called when a new coin lands
   * @param {Function} [params.onActionComplete] - Called after each action
   */
  constructor({
    overlayReelRefs,
    reelRefs,
    collectionLayerContainer,
    symbolWidth,
    symbolHeight,
    reelSpacing,
    onCoinLanded,
    onActionComplete,
  }) {
    this.overlayReelRefs = overlayReelRefs;
    this.reelRefs = reelRefs;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;
    this.onCoinLanded = onCoinLanded;
    this.onActionComplete = onActionComplete;

    this._featureExecutor = new PotCollectionFeatureExecutor({
      overlayReelRefs,
      reelRefs,
      collectionLayerContainer,
      symbolWidth,
      symbolHeight,
      reelSpacing,
    });

    this._cancelled = false;

  }

  /**
   * Pre-warm heavy per-feature assets during an idle window (activation-phase
   * start) so their cold-start cost doesn't stutter mid-sequence. Currently
   * warms the Double feature's pooled 2x label spine.
   */
  prewarm() {
    if (this._featureExecutor) this._featureExecutor.prewarmDoubleLabel();
  }

  // ── Main Processing Loop ────────────────────────────────────────────────────

  /**
   * Process all screen_progress actions sequentially.
   *
   * @param {Array} screenProgress - Array of action objects from BE
   * @param {Function} dispatch - Redux dispatch
   * @param {boolean} [slamStop=false] - Skip animations (initial value)
   * @param {Object} [activatedZeusPosition=null] - Zeus position context
   * @param {Object} [doubleContext=null] - Multi-double context
   * @param {Object} [spinEngine=null] - SpinEngine ref for reactive slamStop checks
   * @param {Set<string>} [pendingZeusPositions=null] - Positions of Zeus coins not yet activated
   * @returns {Promise<void>}
   */
  async processAll(screenProgress, dispatch, slamStop = false, activatedZeusPosition = null, doubleContext = null, spinEngine = null, pendingZeusPositions = null) {
    this._activatedZeusPosition = activatedZeusPosition;
    this._doubleContext = doubleContext;
    this._spinEngine = spinEngine;
    this._pendingZeusPositions = pendingZeusPositions || new Set();

    // Pass spinEngine to feature executor for reactive slamStop checks
    if (spinEngine && this._featureExecutor) {
      this._featureExecutor.setSpinEngine(spinEngine);
    }
    if (!screenProgress || screenProgress.length === 0) {
      return;
    }
    if (this._cancelled) return;

    dispatch(setPotCollectionScreenProgressProcessing(true));


    for (let i = 0; i < screenProgress.length; i++) {
      if (this._cancelled) return;

      // Reactive slamStop check — if player pressed spacebar mid-animation,
      // all subsequent actions execute in slamStop mode
      const effectiveSlamStop = slamStop || (this._spinEngine?._slamStopRequested ?? false);

      const action = screenProgress[i];
      dispatch(setPotCollectionScreenProgressProcessing(true));

      await this._processAction(action, dispatch, effectiveSlamStop);

      if (this._cancelled) return;

      dispatch(advancePotCollectionScreenProgress());
      this.onActionComplete?.(action, i);
    }

    dispatch(setPotCollectionScreenProgressProcessing(false));
  }

  // ── Action Dispatcher ───────────────────────────────────────────────────────

  async _processAction(action, dispatch, slamStop) {
    if (!action || !action.action) {
      return;
    }

    // Per-feature speed override from config (FEATURE_SPEED flags).
    // Config flags force fast mode by default; reactive slamStop still applies on top.
    const featureSpeed = FEATURES_CONFIG.POT_COLLECTION.FEATURE_SPEED || {};

    switch (action.action) {
      case ACTION_TYPES.NEW_COINS:
        await this._handleNewCoins(action, dispatch, slamStop);
        break;

      case ACTION_TYPES.M_REVEALS:
        await this._handleMysteryReveal(action, dispatch, slamStop || featureSpeed.MYSTERY_ALWAYS_FAST);
        break;

      case ACTION_TYPES.D_DOUBLES:
        await this._handleDoubleDoubles(action, dispatch, slamStop || featureSpeed.DOUBLE_ALWAYS_FAST);
        break;

      case ACTION_TYPES.D_REVEALS:
        await this._handleDoubleReveal(action, dispatch, slamStop);
        break;

      case ACTION_TYPES.C_COLLECT:
        await this._handleCollect(action, dispatch, slamStop || featureSpeed.COLLECT_ALWAYS_FAST);
        break;

      default:
        // Unknown action type — skip silently
        break;
    }
  }

  // ── Action Handlers ─────────────────────────────────────────────────────────

  /**
   * new_coins: A coin has landed at [reel, row].
   * Play `in` animation, increment coin count, reset respins to 3.
   */
  async _handleNewCoins(action, dispatch, slamStop) {
    const coords = action.coordinates;
    if (!coords) return;

    // Coordinates can be a single [reel, row] or array of [reel, row] pairs
    const coinList = Array.isArray(coords[0]) ? coords : [coords];

    for (const [reelIndex, symbolIndex] of coinList) {
      if (this._cancelled) return;

      // Show coin's in animation on overlay
      const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);

      if (overlaySymbolRef) {
        // Get the symbolId from the current round screen
        const state = store.getState();
        const currentReels = state.potCollection?.currentRound?.reels;
        const symbolId = currentReels?.[reelIndex]?.[symbolIndex];


        if (symbolId !== undefined && symbolId !== null) {
          if (slamStop) {
            // Instant: show idle immediately
            overlaySymbolRef.showIdleAnimation?.(symbolId);
          } else {
            // Play in animation → transitions to idle
            await new Promise((resolve) => {
              let safetyTid = null;
              const done = () => {
                if (safetyTid) safetyTid.clear();
                resolve();
              };
              overlaySymbolRef.showLandingAnimation?.(symbolId, done);
              safetyTid = new TickerTimeout(done, 1000);
            });
          }

          // Z-index priority — see getSymbolZIndex for full hierarchy
          if (overlaySymbolRef.setZIndex) {
            overlaySymbolRef.setZIndex(getSymbolZIndex(symbolId));
          }
        }
      }

      // Hide static texture
      const reelRef = this.reelRefs?.current?.[reelIndex];
      const symbolRef = reelRef?.getSymbolRef?.(symbolIndex);
      if (symbolRef?.current?.hideSymbolTexture) {
        symbolRef.current.hideSymbolTexture();
      }

      // Increment coin count
      dispatch(incrementPotCollectionCoinCount());

      // Respin counter reset is handled by SpinEngine._handleSymbolStopComplete
      // via applyPotCollectionPendingRespins() on first coin landing (RC pattern).

      this.onCoinLanded?.({ reelIndex, symbolIndex });
    }
  }

  /**
   * m_reveals: Mystery Zeus reveals as a jackpot tier.
   * Delegates to PotCollectionFeatureExecutor.executeMystery().
   */
  async _handleMysteryReveal(action, dispatch, slamStop) {
    const coords = action.coordinates;
    if (!coords || coords.length < 2) {
      return;
    }

    const [reelIndex, symbolIndex] = coords;
    const tier = action.tier || "mini";

    await this._featureExecutor.executeMystery({
      reelIndex,
      symbolIndex,
      tier,
      slamStop,
    });
  }

  /**
   * d_doubles: Double Zeus doubles all coin values on grid.
   * Delegates to PotCollectionFeatureExecutor.executeDouble().
   *
   * Multi-double support (GDD §5.10): When multiple doubles land in one spin,
   * the BE sends ONE final values grid. The FE back-computes intermediate values:
   *   For N doubles, the k-th double's target = finalValue / 2^(N-1-k)
   */
  async _handleDoubleDoubles(action, dispatch, slamStop) {
    const coords = action.coordinates;
    if (!coords) return;

    // Find the Green Zeus position from the grid
    const state = store.getState();
    const currentReels = state.potCollection?.currentRound?.reels
      || state.potCollection?.initialRespinScreen;

    let zeusReelIndex = -1;
    let zeusSymbolIndex = -1;

    // Use the activated position passed from SpinEngine (authoritative)
    if (this._activatedZeusPosition) {
      zeusReelIndex = this._activatedZeusPosition.reelIndex;
      zeusSymbolIndex = this._activatedZeusPosition.symbolIndex;
    } else {
      // Fallback: scan grid for Double Zeus (initial activation only)
      if (currentReels) {
        for (let ri = 0; ri < currentReels.length; ri++) {
          const reel = currentReels[ri];
          if (!Array.isArray(reel)) continue;
          for (let si = 0; si < reel.length; si++) {
            const cellId = parseInt(reel[si], 10);
            if (cellId === 12 || cellId === 20) {
              zeusReelIndex = ri;
              zeusSymbolIndex = si;
              break;
            }
          }
          if (zeusReelIndex >= 0) break;
        }
      }
    }

    if (zeusReelIndex < 0) return;

    // ── Multi-double intermediate value computation ──
    // The BE sends ONE final values grid after ALL doubles have been applied.
    // For N doubles, the k-th double (0-indexed) targets: finalValue / 2^(N-1-k)
    //   E.g. 2 doubles: D₁ targets final/2, D₂ targets final/1 (the final value)
    // For single double (N=1): divisor = 2^0 = 1, no change needed.
    const { doubleIndex = 0, totalDoubles = 1 } = this._doubleContext || {};
    const remainingDoubles = totalDoubles - 1 - doubleIndex;
    const divisor = Math.pow(2, remainingDoubles);

    const affectedCoins = [];
    const coinCoords = Array.isArray(coords[0]) ? coords : [coords];
    const respinValues = state.potCollection?.currentRound?.storage?.values
      || state.potCollection?.initialRespinValues || [];
    const origValues = state.potCollection?.originalValuesGrid
      || state.potCollection?.currentRound?.storage?.original_values || [];

    for (const [ri, si] of coinCoords) {
      // Skip coins at pending (not-yet-activated) Zeus positions —
      // they haven't been revealed yet, so the current Zeus can't affect them.
      const posKey = `${ri}_${si}`;
      if (this._pendingZeusPositions?.has(posKey)) {
        continue;
      }
      const finalValue = respinValues[ri]?.[si] || 0;
      // Back-compute intermediate value for THIS specific double
      const newValue = divisor > 1 ? Math.round(finalValue / divisor) : finalValue;
      // Pre-double value from BE's original_values (before any feature processing)
      const originalValue = origValues[ri]?.[si] || 0;

      affectedCoins.push({ reelIndex: ri, symbolIndex: si, newValue, originalValue });
    }

    const finalZeusValue = respinValues[zeusReelIndex]?.[zeusSymbolIndex] || action.zeus_value || 0;
    const zeusValue = divisor > 1 ? Math.round(finalZeusValue / divisor) : finalZeusValue;

    if (affectedCoins.length > 0) {
      await this._featureExecutor.executeDouble({
        reelIndex: zeusReelIndex,
        symbolIndex: zeusSymbolIndex,
        affectedCoins,
        zeusValue,
        slamStop,
      });
    }
  }

  /**
   * d_reveals: Double Zeus shows its own random value.
   * Sequence: activate_value_green_cd (once) → idle_cd (loop).
   */
  async _handleDoubleReveal(action, dispatch, slamStop) {
    const coords = action.coordinates;
    if (!coords || coords.length < 2) return;

    const [reelIndex, symbolIndex] = coords;
    const state = store.getState();
    const respinValues = state.potCollection?.currentRound?.storage?.values
      || state.potCollection?.initialRespinValues || [];
    const finalValue = respinValues[reelIndex]?.[symbolIndex] || action.value || 0;

    // Apply multi-double divisor — the values grid has the FINAL value after ALL doubles.
    // For the first of N doubles, d_reveals should show finalValue / 2^(N-1-k).
    const { doubleIndex = 0, totalDoubles = 1 } = this._doubleContext || {};
    const remainingDoubles = totalDoubles - 1 - doubleIndex;
    const divisor = Math.pow(2, remainingDoubles);
    const value = divisor > 1 ? Math.round(finalValue / divisor) : finalValue;


    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    if (!overlaySymbolRef) return;

    // Store in _previousValues so the next double's counter animation
    // starts from this intermediate value (not the fallback Math.floor(newValue/2))
    const coinKey = `${reelIndex}_${symbolIndex}`;
    this._featureExecutor._previousValues.set(coinKey, value);

    // Set value text hidden — activate_value_green_cd controls the fade-in via its rgba track.
    // Wait 1 frame after starting the animation then set alpha=1 so the track can reveal it.
    this._featureExecutor._updateOverlayValueText(overlaySymbolRef, reelIndex, symbolIndex, value);

    // Always play the animated reveal — same visual in all speed modes.
    // activate_value_green_cd has a built-in rgba fade-in for the value text.
    this._featureExecutor._setOverlayTextAlpha(overlaySymbolRef, 0);

    const executor = this._featureExecutor;
    const symRef = overlaySymbolRef;
    // Fire-and-forget: d_reveals visual runs concurrently while next actions proceed.
    // Uses spine state directly (setAnimation + addAnimation) to avoid dependency on
    // executor timer infrastructure — the processor may be destroyed before this completes.
    const controller = symRef?.getController?.();
    const spine = controller?.getActiveSpine?.() || controller?.prePositionedSpines?.get("cs");
    if (spine?.state) {
      spine.state.setAnimation(0, "CD_Green/activate_value_green_cd", false);
      // Queue idle_cd to play after activate_value completes — spine handles the transition
      spine.state.addAnimation(0, "CD_Green/idle_cd", true, 0);
      requestAnimationFrame(() => executor?._setOverlayTextAlpha(symRef, 1));
    }
  }

  /**
   * c_collect: Collect Zeus gathers all coin values.
   *
   * BE sends { action: "c_collect", coordinates: [reel, row] } — the Zeus position.
   * Source coins are NOT in the action — we scan the grid's values to find them.
   * Any cell with a non-zero value that isn't the Zeus itself is a source coin.
   *
   * Delegates to PotCollectionFeatureExecutor.executeCollect().
   */
  async _handleCollect(action, dispatch, slamStop) {
    const coords = action.coordinates;
    if (!coords || coords.length < 2) {
      return;
    }

    // Use authoritative position from SpinEngine if available
    const reelIndex = this._activatedZeusPosition?.reelIndex ?? coords[0];
    const symbolIndex = this._activatedZeusPosition?.symbolIndex ?? coords[1];

    // Gather source coins from the grid's value data
    let sourceCoins = [];
    let accumulatedValue = 0;

    if (action.source_coins && action.source_coins.length > 0) {
      // BE explicitly provided source coins (future-proofing)
      sourceCoins = action.source_coins.map((sc) => ({
        reelIndex: sc[0],
        symbolIndex: sc[1],
        value: sc[2] || 0,
      }));
      accumulatedValue = action.total || sourceCoins.reduce((sum, c) => sum + c.value, 0);
    } else {
      // Scan grid: storage.values[reel][row] — non-zero = coin with value.
      // Source coins list is used for visual trail animations.
      // The TOTAL comes from the BE's authoritative value at values[collectReel][collectRow],
      // which the BE computes sequentially: first C sums all non-C coins,
      // second C includes first C's result (GDD: "Multiple coins work one by one,
      // last will add values from previous C coins").
      const state = store.getState();
      const values = state.potCollection?.currentRound?.storage?.values
        || state.potCollection?.initialRespinValues;

      // ── Get the collect coin's own value from the values grid (BE-authoritative) ──
      let authoritativeCollectValue = 0;
      if (values && Array.isArray(values) && values[reelIndex]) {
        authoritativeCollectValue = values[reelIndex][symbolIndex] || 0;
      }

      if (values && Array.isArray(values)) {
        for (let reel = 0; reel < values.length; reel++) {
          if (!Array.isArray(values[reel])) continue;
          for (let row = 0; row < values[reel].length; row++) {
            const val = values[reel][row];
            // Skip zero values (empty/blank) and the collect coin itself
            if (!val || val === 0) continue;
            if (reel === reelIndex && row === symbolIndex) continue;
            // Skip coins at pending (not-yet-activated) Zeus positions —
            // they haven't been activated yet, can't be collected by this Zeus.
            const posKey = `${reel}_${row}`;
            if (this._pendingZeusPositions?.has(posKey)) {
              continue;
            }

            sourceCoins.push({ reelIndex: reel, symbolIndex: row, value: val });
            accumulatedValue += val;
          }
        }
      }

      // ── Use BE-authoritative total ──
      if (authoritativeCollectValue > 0) {
        accumulatedValue = authoritativeCollectValue;
      }

    }

    if (sourceCoins.length > 0) {
      await this._featureExecutor.executeCollect({
        reelIndex,
        symbolIndex,
        sourceCoins,
        accumulatedValue,
        slamStop,
      });
    }
  }



  // ── Resize / Cleanup ────────────────────────────────────────────────────────

  resize(symbolWidth, symbolHeight, reelSpacing) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;
    if (this._featureExecutor) {
      this._featureExecutor.resize(symbolWidth, symbolHeight, reelSpacing);
    }
  }

  cancel() {
    this._cancelled = true;
    if (this._featureExecutor) this._featureExecutor.cancel();
  }

  destroy() {
    this.cancel();
    if (this._featureExecutor) {
      this._featureExecutor.destroy();
      this._featureExecutor = null;
    }
    this._spinEngine = null;
    this.overlayReelRefs = null;
    this.reelRefs = null;
  }
}

export default PotCollectionScreenProgressProcessor;
